import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';
import { FiSun, FiMoon } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import logoImg from '../assets/logo.svg';
import { useTheme } from '../context/ThemeContext';

const navLinks = [
  { name: 'Home', href: '/' },
  { name: 'Services', href: '/#services' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Process', href: '/#process' },
  { name: 'Why Us', href: '/#why-us' },
  { name: 'Testimonials', href: '/#testimonials' },
  { name: 'FAQ', href: '/#faq' },

];

export default function Navbar({ mobileMenuOpen, setMobileMenuOpen }) {
  const { isDark, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 100 }}
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled ? 'bg-white/80 dark:bg-black/60 backdrop-blur-2xl shadow-2xl border-b border-gray-200 dark:border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-24">
        <Link to="/" className="flex items-center space-x-3">
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="flex items-center space-x-3">
            <div className="relative">
              <div className="absolute inset-0 bg-cyan-500 blur-xl opacity-30 animate-pulse rounded-full pointer-events-none" />
              <motion.img src={logoImg} alt="Logo" className="w-14 h-14 object-contain relative z-10"
                style={{ filter: 'drop-shadow(0 0 10px rgba(34,211,238,0.8))' }}
                animate={{ y: [0, -4, 0], scale: [1, 1.05, 1], rotate: [0, 4, -4, 0] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />
            </div>
            <div>
              <div className="text-2xl font-bold tracking-tight">
                <span className="text-gray-900 dark:text-white">bottom</span>
                <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">funnel</span>
              </div>
              <div className="text-[10px] text-gray-500 tracking-widest uppercase font-medium -mt-0.5">Maximizing Conversions</div>
            </div>
          </motion.div>
        </Link>

        {/* Desktop */}
        <div className="hidden lg:flex items-center space-x-1">
          {navLinks.map((link, i) => (
            <Link key={link.name} to={link.href}>
              <motion.div
                initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }} whileHover={{ y: -2 }}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-white transition-all relative group"
              >
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-orange-500 group-hover:w-full transition-all duration-300" />
              </motion.div>
            </Link>
          ))}
          <button
            onClick={toggleTheme}
            className="ml-4 p-2.5 rounded-full bg-gray-200 dark:bg-white/10 text-gray-800 dark:text-white hover:bg-gray-300 dark:hover:bg-white/20 transition-all"
            aria-label="Toggle Dark Mode"
          >
            {isDark ? <FiSun size={18} /> : <FiMoon size={18} />}
          </button>
          <Link to="/contact">
            <motion.div
              whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(14,165,233,0.4)' }}
              whileTap={{ scale: 0.95 }}
              className="ml-4 px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full text-sm font-semibold text-white shadow-lg shadow-cyan-500/30 transition-all"
            >
              Get Started
            </motion.div>
          </Link>
        </div>

        <div className="flex items-center lg:hidden gap-3">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full bg-gray-200 dark:bg-white/10 text-gray-800 dark:text-white transition-all"
          >
            {isDark ? <FiSun size={20} /> : <FiMoon size={20} />}
          </button>
          {/* Mobile toggle */}
          <motion.button whileTap={{ scale: 0.9 }} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-3 rounded-xl bg-gray-200 dark:bg-white/5 backdrop-blur-sm border border-gray-300 dark:border-white/10 text-gray-900 dark:text-white">
            {mobileMenuOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}
            className="lg:hidden bg-white/95 dark:bg-black/95 backdrop-blur-2xl border-t border-gray-200 dark:border-white/5">
            <div className="px-6 py-8 space-y-2">
              {navLinks.map((link, i) => (
                <Link key={link.name} to={link.href} onClick={() => setMobileMenuOpen(false)}>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="block px-5 py-4 text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:text-white hover:bg-white/5 rounded-xl transition-all"
                  >
                    {link.name}
                  </motion.div>
                </Link>
              ))}
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navLinks.length * 0.05 }}
                  className="block mt-4 px-5 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl text-base font-semibold text-white text-center shadow-lg shadow-cyan-500/30">
                  Get Started
                </motion.div>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
